/**
 * Strip conflict markers from file content, keeping the OURS (Generated) side.
 *
 * Two-pass parser that only recognizes replay-specific markers:
 *   Opener:    "<<<<<<< Generated"
 *   Separator: "======="
 *   Closer:    ">>>>>>> Your customization"
 *
 * Pass 1: Scan for complete valid triplets (opener + separator + closer).
 *         Nested openers abandon the current triplet.
 * Pass 2: Build output keeping OURS lines, skipping THEIRS and marker lines.
 */

const CONFLICT_OPENER = "<<<<<<< Generated";
const CONFLICT_SEPARATOR = "=======";
const CONFLICT_CLOSER = ">>>>>>> Your customization";

interface ConflictRange {
    start: number;
    separator: number;
    end: number;
}

function trimCR(line: string): string {
    return line.endsWith("\r") ? line.slice(0, -1) : line;
}

function findConflictRanges(lines: string[]): ConflictRange[] {
    const ranges: ConflictRange[] = [];
    let i = 0;
    while (i < lines.length) {
        if (trimCR(lines[i]) === CONFLICT_OPENER) {
            let separatorIdx = -1;
            let j = i + 1;
            let found = false;
            while (j < lines.length) {
                const trimmed = trimCR(lines[j]);
                if (trimmed === CONFLICT_OPENER) {
                    break; // nested opener, abandon current triplet
                }
                if (separatorIdx === -1 && trimmed === CONFLICT_SEPARATOR) {
                    separatorIdx = j;
                } else if (separatorIdx !== -1 && trimmed === CONFLICT_CLOSER) {
                    ranges.push({ start: i, separator: separatorIdx, end: j });
                    i = j;
                    found = true;
                    break;
                }
                j++;
            }
            if (!found) {
                i++;
                continue;
            }
        }
        i++;
    }
    return ranges;
}

export function stripConflictMarkers(content: string): string {
    const lines = content.split(/\r?\n/);
    const ranges = findConflictRanges(lines);

    if (ranges.length === 0) {
        return content;
    }

    const result: string[] = [];
    let rangeIdx = 0;

    for (let i = 0; i < lines.length; i++) {
        if (rangeIdx < ranges.length) {
            const range = ranges[rangeIdx];
            if (i === range.start) {
                continue;
            }
            if (i > range.start && i < range.separator) {
                result.push(lines[i]);
                continue;
            }
            if (i === range.separator) {
                continue;
            }
            if (i > range.separator && i < range.end) {
                continue;
            }
            if (i === range.end) {
                rangeIdx++;
                continue;
            }
        }
        result.push(lines[i]);
    }

    return result.join("\n");
}

export function hasConflictMarkers(content: string): boolean {
    const lines = content.split(/\r?\n/);
    return findConflictRanges(lines).length > 0;
}

/**
 * Loose substring check: returns true if the content contains either the
 * opener or closer marker text (without verifying a complete triplet).
 *
 * Differs from `hasConflictMarkers` (which performs structural parsing of
 * complete opener+separator+closer triplets). Used at sites where the
 * presence of a marker substring — even a partial or mid-block one — is
 * a signal that something polluted the content (e.g., reconstruction
 * produced a marker-laden result, accumulator state crossed runs, etc.).
 *
 * Accepts `null` / `undefined` for ergonomic use at sites where the
 * content variable may not be set yet.
 */
export function hasConflictMarkerStrings(content: string | null | undefined): boolean {
    if (content == null) return false;
    return content.includes(CONFLICT_OPENER) || content.includes(CONFLICT_CLOSER);
}
